import { useState, useEffect, KeyboardEvent } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { VariableElement } from '../plugins/with-variables';
import { Search, X } from 'lucide-react';

// Mock data for now - this would come from an API later. TODO: @Akshay -- add a real API call here
const VARIABLE_CATEGORIES = [
  {
    name: 'Subscriber',
    variables: [
      { name: 'firstName', description: 'First name of the subscriber' },
      { name: 'lastName', description: 'Last name of the subscriber' },
      { name: 'email', description: 'Email address of the subscriber' },
      { name: 'phoneNumber', description: 'Phone number of the subscriber' }
    ]
  },
  {
    name: 'Message',
    variables: [
      { name: 'messageId', description: 'Unique identifier for the message' },
      { name: 'campaignName', description: 'Name of the campaign' }
    ]
  },
  {
    name: 'Offers',
    variables: [
      { name: 'discountCode', description: 'Personalized discount code' },
      { name: 'discountAmount', description: 'Discount amount' },
      { name: 'expiryDate', description: 'Expiry date of the offer' }
    ]
  }
];

interface VariableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedVariable: VariableElement | null;
  onVariableSelected: (variableName: string, category: string, defaultValue: string | null) => void;
}

export function VariableDialog({
  open,
  onOpenChange,
  selectedVariable,
  onVariableSelected
}: VariableDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [defaultValue, setDefaultValue] = useState<string>('');
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [selectedItem, setSelectedItem] = useState<{ name: string; category: string } | null>(null);

  // Reset state when dialog opens/closes or when editing a variable
  useEffect(() => {
    if (open && selectedVariable) {
      setDefaultValue(selectedVariable.defaultValue ?? '');
      setSelectedItem({ name: selectedVariable.variableName, category: selectedVariable.category });
    } else if (!open) {
      setSearchQuery('');
      setDefaultValue('');
      setSelectedIndex(0);
      setSelectedItem(null);
    }
  }, [open, selectedVariable]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0) {
      const selectedElement = document.getElementById(`variable-${selectedIndex}`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  // Filter variables based on search query
  const filteredCategories = VARIABLE_CATEGORIES.map((category) => ({
    ...category,
    variables: category.variables.filter(
      (variable) =>
        variable.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        variable.description.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter((category) => category.variables.length > 0);

  // Flatten variables for keyboard navigation
  const allVariables = filteredCategories.flatMap((category) =>
    category.variables.map((variable) => ({ ...variable, category: category.name }))
  );

  const handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % allVariables.length);
        const nextVar = allVariables[(selectedIndex + 1) % allVariables.length];
        setSelectedItem({ name: nextVar.name, category: nextVar.category });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + allVariables.length) % allVariables.length);
        const prevVar =
          allVariables[(selectedIndex - 1 + allVariables.length) % allVariables.length];
        setSelectedItem({ name: prevVar.name, category: prevVar.category });
        break;
      case 'Enter':
        e.preventDefault();
        if (e.target instanceof HTMLInputElement) {
          // Don't submit when pressing enter in input fields
          return;
        }
        if (selectedItem) {
          onVariableSelected(selectedItem.name, selectedItem.category, defaultValue || null);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onOpenChange(false);
        break;
    }
  };

  const handleVariableClick = (variable: { name: string; category: string }) => {
    setSelectedItem(variable);
    const idx = allVariables.findIndex(
      (v) => v.name === variable.name && v.category === variable.category
    );
    if (idx !== -1) {
      setSelectedIndex(idx);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" onKeyDown={handleKeyDown}>
        <DialogHeader>
          <DialogTitle>{selectedVariable ? 'Edit Variable' : 'Insert Variable'}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="search">Search Variables</Label>
            <div className="relative">
              <Input
                id="search"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedIndex(0);
                }}
                placeholder="Search by name or description..."
                className="pl-8"
                autoFocus
              />
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedIndex(0);
                  }}
                  className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="h-[320px] overflow-y-auto rounded-md border bg-muted/5">
            <div className="space-y-2 p-2">
              {filteredCategories.map((category) => (
                <div key={category.name} className="space-y-1">
                  <h4 className="px-2 py-1.5 text-sm font-semibold text-foreground/80 bg-muted/50 rounded-md">
                    {category.name}
                  </h4>
                  <div className="space-y-0.5 pl-2">
                    {category.variables.map((variable, _) => {
                      const globalIdx = allVariables.findIndex(
                        (v) => v.name === variable.name && v.category === category.name
                      );
                      const isSelected =
                        selectedItem &&
                        selectedItem.name === variable.name &&
                        selectedItem.category === category.name;
                      return (
                        <button
                          id={`variable-${globalIdx}`}
                          key={variable.name}
                          onClick={() => {
                            handleVariableClick({ name: variable.name, category: category.name });
                          }}
                          onDoubleClick={() => {
                            onVariableSelected(variable.name, category.name, defaultValue || null);
                          }}
                          className={`w-full rounded-md p-2 text-left transition-colors hover:bg-accent/50 ${
                            isSelected
                              ? 'border border-primary bg-primary/5'
                              : 'border border-transparent'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-medium">{variable.name}</div>
                              <div className="text-xs text-muted-foreground">
                                {variable.description}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="defaultValue" className="flex items-center gap-2">
              Default Value
              <span className="text-xs text-muted-foreground font-normal">(Optional)</span>
            </Label>
            <Input
              id="defaultValue"
              value={defaultValue}
              onChange={(e) => {
                setDefaultValue(e.target.value);
              }}
              placeholder="Enter a default value..."
            />
          </div>
        </div>

        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (selectedItem) {
                onVariableSelected(selectedItem.name, selectedItem.category, defaultValue || null);
              }
            }}
            disabled={!selectedItem}
          >
            {selectedVariable ? 'Update' : 'Insert'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
